import router from 'next/router';
import { useContext, useMemo } from 'react';
import { MapMarkerType, ResultsType, MarkerLocation } from '../types';
import { useSelector } from 'react-redux';
import {
  SearchContext,
  SearchContextInterface
} from '../../search-new/context/SearchContext';
import { isEqual } from '../../../helpers/isEqual';
import Supercluster from 'supercluster';

interface MarkersProps {
  mapRef: any;
}

const useMarkers = ({ mapRef }: MarkersProps): MarkerLocation[] => {
  const { searchFiltersState, appliedFiltersCount } = useContext(
    SearchContext
  ) as SearchContextInterface;

  const areas = useSelector((state: any) => state.map.areas);
  const compounds = useSelector((state: any) => state.map.compounds);
  const resultsType = useSelector(
    (state: any) => state.map.resultsType,
    isEqual
  );
  const selectedCompound = useSelector(
    (state: any) => state.map.selectedCompound
  );

  const firstDestination = useSelector(
    (state: any) => state.map.firstDestination
  );
  const secondDestination = useSelector(
    (state: any) => state.map.secondDestination
  );

  const formattedLocations: MarkerLocation[] = useMemo(() => {
    const getLocationType = (
      id: number,
      isFiltered: boolean,
      category: string
    ): MapMarkerType => {
      if (!id) {
        return MapMarkerType.DEFAULT;
      }
      if (category === 'area') {
        return MapMarkerType.AREA;
      }

      if (
        (selectedCompound?.parent_id && selectedCompound?.parent_id === id) ||
        selectedCompound?.id === id
      ) {
        return MapMarkerType.SELECTED;
      } else if (
        appliedFiltersCount > 0 ||
        searchFiltersState.searchFilters.developers.length > 0 ||
        router.asPath.includes('/nawy-now')
      ) {
        if (isFiltered) {
          return MapMarkerType.DEFAULT;
        } else {
          return MapMarkerType.UNFILTERED;
        }
      } else if (firstDestination?.id === id) {
        return MapMarkerType.FIRST_DESTINATION;
      } else if (secondDestination?.id === id) {
        return MapMarkerType.SECOND_DESTINATION;
      }
      return MapMarkerType.DEFAULT;
    };
    const markers = [
      ...(resultsType == ResultsType.COMPOUNDS && compounds.length > 0
        ? compounds.map((compound: any) => ({
            type: 'Feature',
            properties: {
              id: compound?.id,
              name: compound?.name,
              type: getLocationType(
                compound?.id,
                compound?.isFiltered,
                'compound'
              ),
              category: 'compound',
              isFiltered: compound?.isFiltered
            },
            geometry: {
              type: 'Point',
              coordinates: [compound?.coordinates[0], compound?.coordinates[1]]
            }
          }))
        : []),
      ...(resultsType == ResultsType.AREAS && areas?.length > 0
        ? areas.map((area: any) => ({
            type: 'Feature',
            properties: {
              id: area?.id,
              name: area?.name,
              type: getLocationType(area?.id, area?.isFiltered, 'area'),
              category: 'area',
              isFiltered: area?.isFiltered
            },
            geometry: {
              type: 'Point',
              coordinates: [area?.long, area?.lat]
            }
          }))
        : [])
    ];
    const index = new Supercluster({
      radius: 15,
      maxZoom: 14,
      map: props => ({
        isFiltered: props.isFiltered
      }),
      reduce: (accumulated, props) => {
        accumulated.isFiltered = accumulated.isFiltered || props.isFiltered;
      }
    });

    index.load(markers);

    if (!mapRef?.current) {
      return [];
    }

    const bounds = mapRef.current.getBounds()?.toArray().flat();
    const zoom = mapRef.current.getZoom();

    if (!bounds || bounds.length !== 4) {
      return [];
    }

    const clusters = index.getClusters([-180, -85, 180, 85], zoom);

    return clusters.map((feature: any) => {
      if (feature.properties.cluster) {
        return {
          id: feature.id,
          name: resultsType,
          cluster_count: feature.properties.point_count,
          longitude: feature.geometry.coordinates[0],
          latitude: feature.geometry.coordinates[1],
          type: MapMarkerType.CLUSTER,
          category: 'cluster',
          isFiltered: feature.properties.isFiltered,
          coordinates: feature.geometry.coordinates
        };
      } else {
        return {
          id: feature.properties.id,
          name: feature.properties.name,
          longitude: feature.geometry.coordinates[0],
          latitude: feature.geometry.coordinates[1],
          type: feature.properties.type,
          category: feature.properties.category,
          isFiltered: feature.properties.isFiltered,
          coordinates: feature.geometry.coordinates
        };
      }
    });
  }, [
    compounds,
    areas,
    selectedCompound,
    firstDestination,
    secondDestination,
    appliedFiltersCount,
    searchFiltersState.searchFilters,
    mapRef,
    resultsType
  ]);

  return formattedLocations;
};

export default useMarkers;
